import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Course } from '../types';
import { useAuth } from '../context/AuthContext';
import { useCourses } from '../context/CourseContext';
import { ArrowRight, Users } from 'lucide-react';
import { motion } from 'motion/react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { optimizeImageUrl } from '../utils/image';

interface CourseCardProps {
  course: Course;
}

export const CourseCard: React.FC<CourseCardProps> = ({ course }) => {
  const navigate = useNavigate();
  const { profile, isAdmin } = useAuth();
  const isEnrolled = (profile?.enrolledCourses || []).includes(course.id) || isAdmin;

  const handleAction = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (isEnrolled) {
      navigate(`/course/${course.id}/content`);
    } else {
      navigate(`/course/${course.id}`);
    }
  };

  return (
    <motion.div 
      whileHover={{ y: -5 }}
      onClick={handleAction}
      className="bg-zinc-900/50 rounded-xl overflow-hidden transition-all duration-300 flex flex-col border border-zinc-800 group/card w-full max-w-[340px] mx-auto cursor-pointer"
      style={{ height: '440px' }}
    >
      {/* Thumbnail Section */}
      <div className="relative aspect-square overflow-hidden">
        <img 
          src={optimizeImageUrl(course.thumbnail || 'https://picsum.photos/seed/course/800/800', 's400')} 
          alt={course.title}
          width={400}
          height={400}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-700 group-hover/card:scale-110"
          referrerPolicy="no-referrer"
        />
      </div>
      
      <div className="p-5 flex flex-col flex-grow">
        {/* Title */}
        <h3 className="text-lg font-bold text-white mb-2 line-clamp-2 leading-tight">
          {course.title}
        </h3>
        
        {/* Price Section */}
        {!isEnrolled && (
          <div className="mb-4">
            <span className={`text-xl font-bold ${course.isFree || course.price === 0 ? 'text-emerald-400' : 'text-white'}`}>
              {course.isFree || course.price === 0 ? 'ফ্রি (Free Course)' : `৳${course.price}`}
            </span>
          </div>
        )}

        {/* Action Button */}
        <div className="mt-auto">
          <button 
            onClick={handleAction}
            className="w-full bg-[#0f172a] hover:bg-black text-white py-3 rounded-lg font-bold text-sm transition-all border border-zinc-800 active:scale-95"
          >
            {isEnrolled ? 'Continue Learning' : 'Enroll Now'}
          </button>
        </div>
      </div>
    </motion.div>
  );
};
